// Section Variants Index
// Combines all section variant configurations and exports SECTION_VARIANTS
//
// MANDATORY: a new variant must be registered in one of the group files below
// AND that group must be spread into SECTION_VARIANTS here. `getVariantDetails()`
// is the single lookup every consumer uses; an unregistered variant resolves to
// `undefined` and its callers fall back to the raw id.
//
// Consumers include the page builder's settings sidebar and **Widget Management**
// (`/admin/widget-management`), which lists every variant in use across all pages
// and shows `name` as the only human label an editor gets before deleting that
// widget from live pages. A variant with no entry reads as "Variant 1051" there.
//
// Adding a section or variant? See the full checklist at the top of
// ../sectionTypes.ts.

import { BANNER_SECTION_VARIANTS, HEADER_SECTION_VARIANTS } from "./bannerHeaderVariants";
import { NAVIGATION_SECTION_VARIANTS } from "./navigationVariants";
import { CONTENT_SECTION_VARIANTS } from "./contentVariants";
import { CURRICULUM_SECTION_VARIANTS } from "./curriculumVariants";
import { COURSE_SECTION_VARIANTS } from "./courseVariants";
import { PEOPLE_SECTION_VARIANTS } from "./peopleVariants";
import { LAYOUT_SECTION_VARIANTS } from "./layoutVariants";
import { FORM_SECTION_VARIANTS } from "./formVariants";
import { getUniversityVariantDetails } from "../universitySettings";

// Complete mapping with variant details (flattened structure)
export const SECTION_VARIANTS = {
  ...BANNER_SECTION_VARIANTS,
  ...HEADER_SECTION_VARIANTS,
  ...NAVIGATION_SECTION_VARIANTS,
  ...CONTENT_SECTION_VARIANTS,
  ...CURRICULUM_SECTION_VARIANTS,
  ...COURSE_SECTION_VARIANTS,
  ...PEOPLE_SECTION_VARIANTS,
  ...LAYOUT_SECTION_VARIANTS,
  ...FORM_SECTION_VARIANTS,
};

// Helper to get variant details
export const getVariantDetails = (sectionType: string, variantId: string) => {
  const id = variantId != null && variantId !== "" ? String(variantId) : variantId;
  // Check SECTION_VARIANTS first (for page sections)
  const variants = SECTION_VARIANTS[sectionType];
  if (variants) {
    const found = variants.find((v) => String(v.id) === id);
    if (found) return found;
  }

  // Check university settings variants if not found in page sections
  if (sectionType === "university_details") {
    return getUniversityVariantDetails(id as string);
  }

  return undefined;
};

// Re-export individual variant groups for direct imports if needed
export {
  BANNER_SECTION_VARIANTS,
  HEADER_SECTION_VARIANTS,
  NAVIGATION_SECTION_VARIANTS,
  CONTENT_SECTION_VARIANTS,
  CURRICULUM_SECTION_VARIANTS,
  COURSE_SECTION_VARIANTS,
  PEOPLE_SECTION_VARIANTS,
  LAYOUT_SECTION_VARIANTS,
  FORM_SECTION_VARIANTS,
};

